import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Lottie from '../components/Lottie'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { useBackground } from '../contexts/BackgroundContext'
import { cartsService, itemsService, authService, socialService } from '../services/api'
import SearchBar from '../components/SearchBar'
import LiquidGlassButton from '../components/LiquidGlassButton'
import ContextMenu from '../components/ContextMenu'
import CompareCartPicker from '../components/CompareCartPicker'
import LanguageSelector from '../components/LanguageSelector'
import ExtensionDownload from '../components/ExtensionDownload'
import ThemeSwitcher from '../components/ThemeSwitcher'
import ConfirmModal from '../components/ConfirmModal'
import BackgroundPreview from '../components/BackgroundPreview'
import AppDock from '../components/AppDock'
import CartCard from '../components/CartCard'
import Analytics from '../components/Analytics'
import HomePage from '../components/HomePage'
import SocialFeed from '../components/SocialFeed'
import { useTranslation } from '../hooks/useTranslation'
import { useCartsQuery } from '../hooks/useCartsQuery'
import { getUserId } from '../utils/sessionUser'
import { adminRoleState, isAdminDisplay } from '../utils/adminPolicy'
import { ShieldGlyph } from '../components/AdminIcons'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { usePresence } from '../hooks/usePresence'
import { consumeAnalyticsReturn, peekAnalyticsReturn } from '../utils/analyticsPolicy'
import AdminFilterSelect from '../components/AdminFilterSelect'
import './CartsPage.css'

const APP_IDS = ['house', 'carts', 'analytics', 'social', 'settings']
const APP_FADE_OUT_MS = 300
const APP_FADE_IN_MS = 400
const CATEGORY_FADE_OUT_MS = 200
const CATEGORY_FADE_IN_MS = 250

function readNavApp(state) {
  const app = state?.activeApp
  if (APP_IDS.includes(app)) return app
  if (peekAnalyticsReturn()) return 'analytics'
  return 'house'
}

function CreateCartCancelGlyph() {
  return (
    <span className="modal-create-glyph" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="22" height="22">
        <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </span>
  )
}

function HeaderToolsGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="9" cy="7" r="2.1" fill="currentColor" />
      <circle cx="15" cy="12" r="2.1" fill="currentColor" />
      <circle cx="8" cy="17" r="2.1" fill="currentColor" />
    </svg>
  )
}

function CreateCartSubmitGlyph() {
  return (
    <span className="modal-create-glyph modal-create-glyph--cart" aria-hidden="true">
      <img src="/Icon shopping cart.png" alt="" />
      <span className="modal-create-plus">+</span>
    </span>
  )
}

function ReloadGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path d="M4.6 12a7.4 7.4 0 0 1 12.4-5.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M17.2 3.9v4.3h-4.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19.4 12a7.4 7.4 0 0 1-12.4 5.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M6.8 20.1v-4.3h4.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CartsLoadError({ retrying, onRetry }) {
  return (
    <button
      type="button"
      className="carts-load-error"
      onClick={onRetry}
      disabled={retrying}
      aria-busy={retrying}
      aria-label="Réessayer le chargement des paniers"
    >
      <span className={`carts-load-error-icon${retrying ? ' is-spinning' : ''}`}>
        <ReloadGlyph />
      </span>
      <span className="carts-load-error-text">Impossible de charger les paniers</span>
    </button>
  )
}

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg
  return error?.message || fallback
}

function CartsPage() {
  const { logout, user, loading, profileError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { addNotification } = useNotifications()
  const userId = getUserId(user)
  const [searchTerm, setSearchTerm] = useState('')
  const [headerToolsOpen, setHeaderToolsOpen] = useState(false)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createFormError, setCreateFormError] = useState('')
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [newCartName, setNewCartName] = useState('')
  const [newCartBudget, setNewCartBudget] = useState('')
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, cart: null })
  const [selectedCart, setSelectedCart] = useState(null)
  const [selectedCartForCompare, setSelectedCartForCompare] = useState(null)
  const [showCompareModal, setShowCompareModal] = useState(false)
  const reducedMotion = usePrefersReducedMotion()
  const headerToolsPresence = usePresence(headerToolsOpen, { duration: 220, reduced: reducedMotion })
  const startApp = readNavApp(location.state)
  const [selectedUserCategory, setSelectedUserCategory] = useState(startApp === 'settings' ? 'parametres' : 'profil')
  const [displayedUserCategory, setDisplayedUserCategory] = useState(startApp === 'settings' ? 'parametres' : 'profil')
  const [isUserCategoryTransitioning, setIsUserCategoryTransitioning] = useState(false)
  const [playCategoryEnter, setPlayCategoryEnter] = useState(false)
  const [userAnimation, setUserAnimation] = useState(null)
  const [houseAnimation, setHouseAnimation] = useState(null)
  const [personalizeAnimation, setPersonalizeAnimation] = useState(null)
  const [settingsAnimation, setSettingsAnimation] = useState(null)
  const [securityAnimation, setSecurityAnimation] = useState(null)
  const [privacyAnimation, setPrivacyAnimation] = useState(null)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  const [priceChangeNotifications, setPriceChangeNotifications] = useState(true)
  const [priceUpdateNotifications, setPriceUpdateNotifications] = useState(true)
  const [errorNotifications, setErrorNotifications] = useState(true)
  const [budgetExceededNotifications, setBudgetExceededNotifications] = useState(() => {
    return localStorage.getItem('budgetExceededNotifications') !== 'false'
  })
  const [productSavedNotifications, setProductSavedNotifications] = useState(() => {
    return localStorage.getItem('productSavedNotifications') !== 'false'
  })
  const [productImportedNotifications, setProductImportedNotifications] = useState(() => {
    return localStorage.getItem('productImportedNotifications') !== 'false'
  })
  const [defaultPriceUpdateMethod, setDefaultPriceUpdateMethod] = useState(() => {
    return localStorage.getItem('defaultPriceUpdateMethod') || 'automatique'
  })
  
  // Paramètres de confidentialité - charger depuis le profil utilisateur
  const { data: userProfileData } = useQuery({
    queryKey: ['social-user-profile', user?.id],
    queryFn: () => socialService.getUserProfile(user?.id),
    enabled: !!user?.id,
    retry: false
  })
  
  const [postVisibility, setPostVisibility] = useState(() => {
    return localStorage.getItem('postVisibility') || 'public'
  })
  const [profileVisibility, setProfileVisibility] = useState(() => {
    return localStorage.getItem('profileVisibility') || 'public'
  })
  const [friendsVisibility, setFriendsVisibility] = useState(() => {
    return localStorage.getItem('friendsVisibility') || 'public'
  })
  
  // Mettre à jour les états quand le profil est chargé
  useEffect(() => {
    if (userProfileData?.user) {
      const postVis = userProfileData.user.post_visibility || 'public'
      const profileVis = userProfileData.user.profile_visibility || 'public'
      const friendsVis = userProfileData.friends_visibility || 'public'
      setPostVisibility(postVis)
      setProfileVisibility(profileVis)
      setFriendsVisibility(friendsVis)
      localStorage.setItem('postVisibility', postVis)
      localStorage.setItem('profileVisibility', profileVis)
      localStorage.setItem('friendsVisibility', friendsVis)
    }
  }, [userProfileData])
  const { selectedBackground, setSelectedBackground } = useBackground()
  const [activeApp, setActiveApp] = useState(startApp) // 'house', 'carts', 'analytics', 'social', 'settings'
  const [displayedApp, setDisplayedApp] = useState(startApp)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const showUserProfile = displayedApp === 'settings'

  useEffect(() => {
    if (!headerToolsOpen) return undefined
    const onPointerDown = (event) => {
      if (event.target.closest('.header-mobile-tools, .header-more-btn')) return
      setHeaderToolsOpen(false)
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setHeaderToolsOpen(false)
    }
    const onViewport = () => {
      if (window.matchMedia('(min-width: 769px)').matches) setHeaderToolsOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', onViewport)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onViewport)
    }
  }, [headerToolsOpen])
  const previousAppRef = useRef(startApp === 'settings' ? 'house' : startApp)
  const appTransitionRef = useRef({ exit: null, enter: null })
  const categoryTransitionRef = useRef({ exit: null, enter: null })
  const selectAppRef = useRef(null)

  const clearAppTransition = useCallback(() => {
    if (appTransitionRef.current.exit) clearTimeout(appTransitionRef.current.exit)
    if (appTransitionRef.current.enter) clearTimeout(appTransitionRef.current.enter)
    appTransitionRef.current = { exit: null, enter: null }
  }, [])

  const clearCategoryTransition = useCallback(() => {
    if (categoryTransitionRef.current.exit) clearTimeout(categoryTransitionRef.current.exit)
    if (categoryTransitionRef.current.enter) clearTimeout(categoryTransitionRef.current.enter)
    categoryTransitionRef.current = { exit: null, enter: null }
  }, [])

  const selectApp = useCallback((appId, options = {}) => {
    if (!APP_IDS.includes(appId)) return
    if (appId === activeApp) return

    if (appId === 'settings') {
      if (options.category) {
        setSelectedUserCategory(options.category)
        setDisplayedUserCategory(options.category)
      }
      setPlayCategoryEnter(false)
      if (displayedApp !== 'settings') {
        previousAppRef.current = displayedApp || 'house'
      }
    }

    clearAppTransition()

    if (reducedMotion) {
      setActiveApp(appId)
      setDisplayedApp(appId)
      setIsTransitioning(false)
      return
    }

    setIsTransitioning(true)
    setActiveApp(appId)
    appTransitionRef.current.exit = setTimeout(() => {
      setDisplayedApp(appId)
      appTransitionRef.current.enter = setTimeout(() => {
        setIsTransitioning(false)
      }, APP_FADE_IN_MS)
    }, displayedApp ? APP_FADE_OUT_MS : 0)
  }, [activeApp, displayedApp, reducedMotion, clearAppTransition])

  selectAppRef.current = selectApp

  const switchUserCategory = useCallback((category) => {
    if (selectedUserCategory === category || isUserCategoryTransitioning) return

    clearCategoryTransition()
    setPlayCategoryEnter(false)
    setIsUserCategoryTransitioning(true)
    setSelectedUserCategory(category)

    if (reducedMotion) {
      setDisplayedUserCategory(category)
      setIsUserCategoryTransitioning(false)
      return
    }

    categoryTransitionRef.current.exit = setTimeout(() => {
      setDisplayedUserCategory(category)
      setPlayCategoryEnter(true)
      categoryTransitionRef.current.enter = setTimeout(() => {
        setIsUserCategoryTransitioning(false)
      }, CATEGORY_FADE_IN_MS)
    }, CATEGORY_FADE_OUT_MS)
  }, [selectedUserCategory, isUserCategoryTransitioning, reducedMotion, clearCategoryTransition])

  // Retour depuis une autre route : animer l'entrée si la page était déjà montée.
  useEffect(() => {
    const next = location.state?.activeApp
    if (!next || !APP_IDS.includes(next)) return
    navigate(location.pathname, { replace: true, state: {} })
    if (next === startApp && displayedApp === next && activeApp === next) return
    if (next === 'settings') {
      selectAppRef.current?.('settings', { category: 'parametres' })
    } else {
      selectAppRef.current?.(next)
    }
  }, [location.state, location.pathname, navigate, startApp, displayedApp, activeApp])

  useEffect(() => {
    consumeAnalyticsReturn()
  }, [])

  // À l'entrée dans la section réseau social : rafraîchir les données pour limiter paniers manquants / chargement vacant
  useEffect(() => {
    if (displayedApp === 'social') {
      queryClient.refetchQueries({
        predicate: (query) => query.queryKey[0] === 'social' || query.queryKey[0] === 'shared-cart'
      })
    }
  }, [displayedApp, queryClient])

  // Liste des backgrounds disponibles
  const availableBackgrounds = [
    { id: 'default', name: 'Par défaut', description: 'Fond animé par défaut' },
    { id: 'bubble-float', name: 'Bulles flottantes', description: 'Bulles animées qui flottent', folder: 'Bubble Float' },
    { id: 'shooting-stars', name: 'Étoiles filantes', description: 'Étoiles filantes animées', folder: 'Shooting stars' },
    { id: 'gradient-waves', name: 'Vagues dégradées', description: 'Vagues avec dégradé', folder: 'Gradient background with waves' },
    { id: 'foil-stamping', name: 'Effet foil', description: 'Effet de gaufrage métallique', folder: 'Foil Stamping Effect' },
    { id: 'flashlight-pattern', name: 'Motif lampe torche', description: 'Motif animé de lampe torche', folder: 'Animated Flashlight Pattern Backgroundexternal' },
    { id: 'day-sunset-night', name: 'Cycle Jour-Coucher-Nuit', description: 'Background qui change selon l\'heure de la journée', folder: 'Day-Sunset-Night Cycle' },
    { id: 'windows-11-colorful', name: 'Windows 11 Colorful', description: 'Fond d\'écran officiel Windows 11 coloré', folder: 'Windows Wallpapers', type: 'image', image: 'windows-11-stock-official-colorful-3840x2160-5658.jpg' },
    { id: 'windows-11-dark-abstract', name: 'Windows 11 Dark Abstract', description: 'Fond d\'écran abstrait sombre Windows 11', folder: 'Windows Wallpapers', type: 'image', image: 'windows-11-dark-mode-abstract-background-black-background-3840x2160-8710.jpg' },
    { id: 'anime-town-rainfall', name: 'Anime Town Rainfall', description: 'Ville animée sous la pluie', folder: 'Windows Wallpapers', type: 'video', video: 'anime-town-rainfall.1920x1080.mp4' },
    { id: 'backyard', name: 'Backyard', description: 'Jardin animé', folder: 'Windows Wallpapers', type: 'video', video: 'backyard.1920x1080.mp4' },
    { id: 'lofi-coffee-shop', name: 'Lo-Fi Coffee Shop', description: 'Café Lo-Fi animé', folder: 'Windows Wallpapers', type: 'video', video: 'lofi-coffee-shop.1920x1080.mp4' }
  ]
  const lottieRef = useRef(null)
  const sidebarLottieRef = useRef(null)
  const titleLottieRef = useRef(null)
  const personalizeSidebarLottieRef = useRef(null)
  const personalizeTitleLottieRef = useRef(null)
  const settingsSidebarLottieRef = useRef(null)
  const settingsTitleLottieRef = useRef(null)
  const securitySidebarLottieRef = useRef(null)
  const securityTitleLottieRef = useRef(null)
  const clickTimeoutRef = useRef(null)
  const longPressTimerRef = useRef(null)



  // Charger les animations Lottie
  useEffect(() => {
    fetch('/user_animation.json')
      .then(response => response.json())
      .then(data => setUserAnimation(data))
      .catch(error => console.error('Erreur lors du chargement de l\'animation:', error))
    
    fetch('/house_animation.json')
      .then(response => response.json())
      .then(data => setHouseAnimation(data))
      .catch(error => console.error('Erreur lors du chargement de l\'animation house:', error))
    
    fetch('/personalize_animation.json')
      .then(response => response.json())
      .then(data => setPersonalizeAnimation(data))
      .catch(error => console.error('Erreur lors du chargement de l\'animation personalize:', error))
    
    fetch('/settings_animation.json')
      .then(response => response.json())
      .then(data => setSettingsAnimation(data))
      .catch(error => console.error('Erreur lors du chargement de l\'animation settings:', error))
    
    fetch('/Security_animation.json')
      .then(response => response.json())
      .then(data => setSecurityAnimation(data))
      .catch(error => console.error('Erreur lors du chargement de l\'animation security:', error))
  }, [])

  // Écouter l'événement pour ouvrir le modal de création de panier depuis la page d'accueil
  useEffect(() => {
    const handleOpenCreateCartModal = () => {
      setCreateFormError('')
      setShowCreateModal(true)
    }

    window.addEventListener('openCreateCartModal', handleOpenCreateCartModal)

    return () => {
      window.removeEventListener('openCreateCartModal', handleOpenCreateCartModal)
    }
  }, [])

  const handleUserButtonClick = () => {
    if (activeApp === 'settings') {
      const previous = previousAppRef.current === 'settings' ? 'house' : previousAppRef.current
      selectApp(previous || 'house')
    } else {
      selectApp('settings')
    }
  }

  const handleDockSelect = (appId) => {
    if (appId === 'settings') {
      selectApp('settings', { category: 'parametres' })
      return
    }
    selectApp(appId)
  }

  const appPaneClass = (variant) => {
    const leaving = isTransitioning && displayedApp !== activeApp
    return [
      'app-content',
      variant === 'settings' ? 'settings-app-pane' : '',
      leaving ? 'fade-out' : 'fade-in',
    ].filter(Boolean).join(' ')
  }

  const userCategoryClass = () => {
    const leaving = isUserCategoryTransitioning && displayedUserCategory !== selectedUserCategory
    if (leaving) return 'user-category-content fade-out'
    if (playCategoryEnter) return 'user-category-content fade-in'
    return 'user-category-content'
  }

  // Déclencher l'animation lors du changement d'état
  useEffect(() => {
    if (lottieRef.current) {
      const lottieInstance = lottieRef.current.animationItem || lottieRef.current
      if (lottieInstance && lottieInstance.goToAndPlay) {
        lottieInstance.goToAndPlay(0, true)
      }
    }
  }, [showUserProfile])

  // Nettoyer les timeouts lors du démontage
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
      }
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
      }
      if (appTransitionRef.current.exit) clearTimeout(appTransitionRef.current.exit)
      if (appTransitionRef.current.enter) clearTimeout(appTransitionRef.current.enter)
      if (categoryTransitionRef.current.exit) clearTimeout(categoryTransitionRef.current.exit)
      if (categoryTransitionRef.current.enter) clearTimeout(categoryTransitionRef.current.enter)
    }
  }, [])

  const {
    data: cartsData,
    isLoading,
    isFetching,
    isError: isCartsError,
    error: cartsError,
    refetch: refetchCarts,
  } = useCartsQuery()
  const carts = cartsData ?? []
  const cartsRetryLock = useRef(false)

  const retryCarts = useCallback(() => {
    if (cartsRetryLock.current || isFetching) return
    cartsRetryLock.current = true
    Promise.resolve(refetchCarts()).finally(() => {
      cartsRetryLock.current = false
    })
  }, [isFetching, refetchCarts])

  const createMutation = useMutation({
    mutationFn: ({ name, budget }) => cartsService.create(name, budget ? parseFloat(budget) : null),
    onSuccess: () => {
      queryClient.invalidateQueries(['carts'])
      setShowCreateModal(false)
      setNewCartName('')
      setNewCartBudget('')
      setCreateFormError('')
    },
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, name }) => cartsService.update(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries(['carts'])
      setShowRenameModal(false)
      setSelectedCart(null)
      setNewCartName('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (cartId) => cartsService.delete(cartId),
    onSuccess: (_data, cartId) => {
      queryClient.setQueryData(['carts', userId], (previous) => (
        Array.isArray(previous) ? previous.filter((cart) => cart.id !== cartId) : previous
      ))
      queryClient.removeQueries({ queryKey: ['items', cartId] })
      queryClient.invalidateQueries({ queryKey: ['carts', userId] })
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['carts', userId] })
      addNotification({
        type: 'error',
        message: getApiErrorMessage(error, 'Impossible de supprimer le panier.'),
      })
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: async (cart) => {
      const payload = cart.name ? { name: `${cart.name} (copie)` } : {}
      return cartsService.duplicate(cart.id, payload)
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries(['carts'])
      if (data?.new_cart?.id) {
        queryClient.invalidateQueries(['items', data.new_cart.id])
        try {
          const freshItems = await itemsService.list(data.new_cart.id)
          queryClient.setQueryData(['items', data.new_cart.id], freshItems)
        } catch (err) {
          console.error('Erreur lors du rafraîchissement des items du panier copié:', err)
        }
      }
      const itemsCount = data?.new_cart?.items_count ?? data?.stats?.created_items ?? 0
      const imagesCopied = data?.stats?.copied_images ?? 0
      addNotification({
        type: 'success',
        message: `Panier dupliqué avec ${itemsCount} produit(s) ! Images copiées: ${imagesCopied}.`
      })
    },
    onError: (error) => {
      console.error('Erreur lors de la duplication du panier:', error)
      addNotification({
        type: 'error',
        message: 'Erreur lors de la duplication du panier'
      })
    },
  })

  const filteredCarts = carts.filter(cart =>
    cart.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const openCreateModal = () => {
    createMutation.reset()
    setCreateFormError('')
    setShowCreateModal(true)
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    createMutation.reset()
    setCreateFormError('')
  }

  const handleCreateCart = (e) => {
    e.preventDefault()
    if (createMutation.isPending) return
    if (!newCartName.trim()) {
      setCreateFormError('Veuillez saisir un nom de panier.')
      return
    }
    setCreateFormError('')
    const budget = newCartBudget.trim() ? parseFloat(newCartBudget.trim()) : null
    createMutation.mutate({ name: newCartName.trim(), budget })
  }

  const handleRenameCart = (e) => {
    e.preventDefault()
    if (newCartName.trim() && selectedCart) {
      renameMutation.mutate({ id: selectedCart.id, name: newCartName.trim() })
    }
  }

  const handleDeleteCart = async (cart) => {
    const confirmed = await new Promise((resolve) => {
      setConfirmModal({
        isOpen: true,
        title: t('confirmDelete') || 'Confirmation',
        message: `${t('confirmDelete')} « ${cart.name} » ?`,
        onConfirm: () => {
          resolve(true)
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
        },
        onCancel: () => {
          resolve(false)
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
        }
      })
    })
    if (confirmed) {
      deleteMutation.mutate(cart.id)
    }
  }

  const handleDuplicateCart = (cart) => {
    duplicateMutation.mutate(cart)
  }

  const handleContextMenu = (e, cart) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      cart: cart
    })
  }

  const handleCloseContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, cart: null })
  }

  const handleContextMenuAction = (action) => {
    const cart = contextMenu.cart
    handleCloseContextMenu()

    switch (action) {
      case 'compare':
        setSelectedCartForCompare(cart)
        setShowCompareModal(true)
        break
      case 'duplicate':
        handleDuplicateCart(cart)
        break
      case 'rename':
        setSelectedCart(cart)
        setNewCartName(cart?.name || '')
        setShowRenameModal(true)
        break
      case 'delete':
        handleDeleteCart(cart)
        break
      default:
        break
    }
  }
  
  const handleCompareCart = (cart1, cart2) => {
    if (cart1 && cart2) {
      navigate(`/compare?cart1=${cart1.id}&cart2=${cart2.id}`)
    }
  }
  
  const handleLongPress = (e, cart) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Annuler le clic simple
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
      clickTimeoutRef.current = null
    }
    
    // Ouvrir le menu contextuel
    setContextMenu({
      visible: true,
      x: e.clientX || e.touches?.[0]?.clientX || 0,
      y: e.clientY || e.touches?.[0]?.clientY || 0,
      cart: cart
    })
  }
  
  const handleMouseDown = (e, cart) => {
    // Démarrer le timer pour le long press (500ms)
    longPressTimerRef.current = setTimeout(() => {
      handleLongPress(e, cart)
    }, 500)
  }
  
  const handleMouseUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }
  
  const handleMouseLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  const handleCardClick = (e, cart) => {
    // Ne pas naviguer si on clique sur un bouton ou ses enfants
    const target = e.target
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return
    }
    
    // Annuler la navigation si un double-clic est détecté
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
      clickTimeoutRef.current = null
      return
    }
    
    // Attendre un peu pour détecter un double-clic avant de naviguer
    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null
      navigate(`/carts/${cart.id}`)
    }, 300)
  }

  const handleDoubleClick = (e, cart) => {
    e.stopPropagation()
    e.preventDefault()
    
    // Annuler la navigation prévue par le clic simple
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
      clickTimeoutRef.current = null
    }
    
    // Ne pas déclencher l'édition si on double-clique sur un bouton ou ses enfants
    const target = e.target
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return
    }
    
    setSelectedCart(cart)
    setNewCartName(cart.name)
    setShowRenameModal(true)
  }

  return (
    <div className="carts-page" onClick={handleCloseContextMenu}>
      <svg style={{ display: 'none' }}>
        <filter id="glass-distortion">
          <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="77" />
        </filter>
        <filter id="lg-dist" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.008 0.008" numOctaves="2" seed="92" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="2" result="blurred" />
          <feDisplacementMap in="SourceGraphic" in2="blurred" scale="70" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id="goo">
          <feGaussianBlur
            result="SvgjsFeGaussianBlur1000"
            in="SourceGraphic"
            stdDeviation="13"
          />
          <feColorMatrix
            result="SvgjsFeColorMatrix1001"
            in="SvgjsFeGaussianBlur1000"
            values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 13 -10
            "
            type="matrix"
          />
          <feComposite
            result="SvgjsFeComposite1002"
            in="SvgjsFeColorMatrix1001"
            operator="atop"
          />
        </filter>
        <filter id="remove-black" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      -255 -255 -255 0 1"
            result="black-pixels"
          />
          <feMorphology
            in="black-pixels"
            operator="dilate"
            radius="0.5"
            result="smoothed"
          />
          <feComposite in="SourceGraphic" in2="smoothed" operator="out" />
        </filter>
      </svg>

      <header className={`carts-header${headerToolsOpen ? ' is-tools-open' : ''}`}>
        <div className="header-container">
          <div className="header-cluster header-cluster-start">
            <span className="header-brand-mark-frame">
              <img
                src="/Logo_Camit_Blanc_Horizontal.png"
                alt="Cam’it"
                className="header-brand-mark"
              />
            </span>
            <div className="header-desktop-theme">
              <ThemeSwitcher />
            </div>
            <SearchBar
              placeholder={t('searchCart')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              globalSearch={true}
            />
          </div>
          <div className="header-cluster header-cluster-end">
            <div className="header-desktop-aux">
              <LanguageSelector position="right" />
              <ExtensionDownload variant="header" />
            </div>
            <LiquidGlassButton
              onClick={() => setHeaderToolsOpen((open) => !open)}
              className="btn-user-icon header-more-btn"
              aria-label={headerToolsOpen ? 'Fermer les réglages' : 'Ouvrir les réglages'}
              aria-expanded={headerToolsOpen}
              aria-controls="header-mobile-tools"
            >
              <HeaderToolsGlyph />
            </LiquidGlassButton>
            <LiquidGlassButton 
              onClick={handleUserButtonClick}
              className="btn-user-icon"
              aria-label={showUserProfile ? "Retour au menu principal" : "Profil utilisateur"}
            >
              {showUserProfile ? (
                houseAnimation ? (
                  <div className="lottie-container">
                    <Lottie 
                      lottieRef={lottieRef}
                      animationData={houseAnimation} 
                      loop={false}
                      autoplay={false}
                      style={{ width: 24, height: 24 }}
                      onComplete={() => {
                        if (lottieRef.current) {
                          const lottieInstance = lottieRef.current.animationItem || lottieRef.current
                          if (lottieInstance && lottieInstance.goToAndStop) {
                            lottieInstance.goToAndStop(0, true)
                          }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <img src="/Icon house.png" alt="Retour au menu principal" />
                )
              ) : (
                userAnimation ? (
                  <div className="lottie-container">
                    <Lottie 
                      lottieRef={lottieRef}
                      animationData={userAnimation} 
                      loop={false}
                      autoplay={false}
                      style={{ width: 24, height: 24 }}
                      onComplete={() => {
                        if (lottieRef.current) {
                          const lottieInstance = lottieRef.current.animationItem || lottieRef.current
                          if (lottieInstance && lottieInstance.goToAndStop) {
                            lottieInstance.goToAndStop(0, true)
                          }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <img src="/Icon person.png" alt="Profil utilisateur" />
                )
              )}
            </LiquidGlassButton>
            <LiquidGlassButton 
              onClick={logout}
              className="btn-logout-icon"
              aria-label="Déconnexion"
            >
              <img src="/Icon logout.png" alt="Déconnexion" />
            </LiquidGlassButton>
          </div>
        </div>
        {headerToolsPresence.mounted ? (
          <div
            id="header-mobile-tools"
            className={`header-mobile-tools${headerToolsPresence.leaving ? ' is-leaving' : ''}${reducedMotion ? ' is-reduced' : ''}`}
            role="dialog"
            aria-label="Réglages"
          >
            <div className="header-tools-frost" aria-hidden="true" />
            <div className="header-tools-body">
              <ThemeSwitcher name="theme-mobile" variant="compact" />
              <LanguageSelector position="left" />
              <ExtensionDownload variant="header" />
              <button type="button" className="header-tools-logout" onClick={logout} aria-label="Déconnexion" title="Déconnexion">
                <img src="/Icon logout.png" alt="" />
              </button>
            </div>
          </div>
        ) : null}
      </header>

      <div className="carts-container">
            {displayedApp === 'carts' && (
              <div className={appPaneClass()}>
                <div className="banner glass-banner">
                  <div className="banner-content">
                    <h2>{t('manageBudgets')}</h2>
                    <p>{t('withImagesLinks')}</p>
                  </div>
                  <LiquidGlassButton 
                    onClick={openCreateModal}
                    className="btn-new-cart btn-new-cart--small"
                  >
                    {t('newCart')}
                  </LiquidGlassButton>
                </div>

                {isLoading && !cartsData ? (
                  <div className="loading">Chargement...</div>
                ) : isCartsError && !cartsData ? (
                  <>
                    <div className="empty-state carts-load-error-desktop">
                      <h3>Impossible de charger les paniers</h3>
                      <p>{cartsError?.response?.data?.detail || cartsError?.message || 'Erreur de récupération'}</p>
                      <LiquidGlassButton onClick={retryCarts} className="btn-create-first">
                        Réessayer
                      </LiquidGlassButton>
                    </div>
                    <CartsLoadError retrying={isFetching} onRetry={retryCarts} />
                  </>
                ) : filteredCarts.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🗂️</div>
                    <h3>{t('noCarts')}</h3>
                    <p>{t('createFirstCart')}</p>
                    <LiquidGlassButton 
                      onClick={openCreateModal}
                      className="btn-create-first"
                    >
                      {t('createCart')}
                    </LiquidGlassButton>
                  </div>
                ) : (
                  <>
                    {isCartsError && cartsData ? (
                      <>
                        <div className="empty-state carts-load-error-desktop carts-load-error-desktop--refresh">
                          <p>Échec de l’actualisation. Les derniers paniers de ce compte restent affichés.</p>
                          <LiquidGlassButton onClick={retryCarts}>Réessayer</LiquidGlassButton>
                        </div>
                        <CartsLoadError retrying={isFetching} onRetry={retryCarts} />
                      </>
                    ) : null}
                  <div className="carts-grid">
                    {filteredCarts.map(cart => (
                      <CartCard
                        key={cart.id}
                        cart={cart}
                        subtitle={`${t('modified')} : ${cart.updated_at ? new Date(cart.updated_at).toLocaleDateString('fr-FR') : '—'}`}
                        totalLabel={`${t('totalEstimated')} : € ${cart.total?.toFixed(2) || '0.00'}`}
                        menu={(
                          <button
                            className="cart-menu-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleContextMenu(e, cart)
                            }}
                          >
                            ⋯
                          </button>
                        )}
                        onClick={(e) => handleCardClick(e, cart)}
                        onDoubleClick={(e) => handleDoubleClick(e, cart)}
                        onContextMenu={(e) => handleContextMenu(e, cart)}
                        onMouseDown={(e) => handleMouseDown(e, cart)}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onTouchStart={(e) => handleMouseDown(e, cart)}
                        onTouchEnd={handleMouseUp}
                      />
                    ))}
                  </div>
                  </>
                )}
              </div>
            )}

            {displayedApp === 'house' && (
              <div className={appPaneClass()}>
                <HomePage onNavigate={(appId) => selectApp(appId)} />
              </div>
            )}

            {displayedApp === 'analytics' && (
              <div className={appPaneClass()}>
                <Analytics />
              </div>
            )}

            {displayedApp === 'social' && (
              <div className={appPaneClass()}>
                <SocialFeed />
              </div>
            )}

            {displayedApp === 'settings' && (
              <div className={appPaneClass('settings')} data-app-pane="settings">
          <div className="user-profile-content">
            <div className="settings-section-select">
              <AdminFilterSelect
                id="settings-section-trigger"
                className="settings-section-picker"
                label="Rubrique"
                value={selectedUserCategory}
                options={[
                  { value: 'profil', label: 'Profil' },
                  { value: 'personnaliser', label: 'Personnaliser' },
                  { value: 'parametres', label: 'Paramètres' },
                  { value: 'securite', label: 'Sécurité' },
                  ...(adminRoleState(user, { loading, profileError }) === 'admin' && isAdminDisplay(user)
                    ? [{ value: 'admin', label: 'Administration' }]
                    : []),
                ]}
                onChange={(next) => {
                  if (next === 'admin') {
                    navigate('/admin')
                    return
                  }
                  switchUserCategory(next)
                }}
              />
            </div>
            <div className="user-sidebar">
              <div className="user-sidebar-header">
                <h3>Menu</h3>
              </div>
              <nav className="user-sidebar-nav">
                <button
                  className={`user-nav-item ${selectedUserCategory === 'profil' ? 'active' : ''}`}
                  onClick={() => {
                    switchUserCategory('profil')
                    // Déclencher l'animation dans le menu
                    if (sidebarLottieRef.current) {
                      const lottieInstance = sidebarLottieRef.current.animationItem || sidebarLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                    // Déclencher l'animation dans le titre
                    if (titleLottieRef.current) {
                      const lottieInstance = titleLottieRef.current.animationItem || titleLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                  }}
                >
                  {userAnimation && (
                    <div className="user-nav-icon">
                      <Lottie 
                        lottieRef={sidebarLottieRef}
                        animationData={userAnimation} 
                        loop={false}
                        autoplay={false}
                        style={{ width: 20, height: 20 }}
                        onComplete={() => {
                          if (sidebarLottieRef.current) {
                            const lottieInstance = sidebarLottieRef.current.animationItem || sidebarLottieRef.current
                            if (lottieInstance && lottieInstance.goToAndStop) {
                              lottieInstance.goToAndStop(0, true)
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                  <span>Profil</span>
                </button>
                <button
                  className={`user-nav-item ${selectedUserCategory === 'personnaliser' ? 'active' : ''}`}
                  onClick={() => {
                    switchUserCategory('personnaliser')
                    // Déclencher l'animation dans le menu
                    if (personalizeSidebarLottieRef.current) {
                      const lottieInstance = personalizeSidebarLottieRef.current.animationItem || personalizeSidebarLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                    // Déclencher l'animation dans le titre
                    if (personalizeTitleLottieRef.current) {
                      const lottieInstance = personalizeTitleLottieRef.current.animationItem || personalizeTitleLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                  }}
                >
                  {personalizeAnimation && (
                    <div className="user-nav-icon">
                      <Lottie 
                        lottieRef={personalizeSidebarLottieRef}
                        animationData={personalizeAnimation} 
                        loop={false}
                        autoplay={false}
                        style={{ width: 20, height: 20 }}
                        onComplete={() => {
                          if (personalizeSidebarLottieRef.current) {
                            const lottieInstance = personalizeSidebarLottieRef.current.animationItem || personalizeSidebarLottieRef.current
                            if (lottieInstance && lottieInstance.goToAndStop) {
                              lottieInstance.goToAndStop(0, true)
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                  <span>Personnaliser</span>
                </button>
                <button
                  className={`user-nav-item ${selectedUserCategory === 'parametres' ? 'active' : ''}`}
                  onClick={() => {
                    switchUserCategory('parametres')
                    // Déclencher l'animation dans le menu
                    if (settingsSidebarLottieRef.current) {
                      const lottieInstance = settingsSidebarLottieRef.current.animationItem || settingsSidebarLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                    // Déclencher l'animation dans le titre
                    if (settingsTitleLottieRef.current) {
                      const lottieInstance = settingsTitleLottieRef.current.animationItem || settingsTitleLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                  }}
                >
                  {settingsAnimation && (
                    <div className="user-nav-icon">
                      <Lottie 
                        lottieRef={settingsSidebarLottieRef}
                        animationData={settingsAnimation} 
                        loop={false}
                        autoplay={false}
                        style={{ width: 20, height: 20 }}
                        onComplete={() => {
                          if (settingsSidebarLottieRef.current) {
                            const lottieInstance = settingsSidebarLottieRef.current.animationItem || settingsSidebarLottieRef.current
                            if (lottieInstance && lottieInstance.goToAndStop) {
                              lottieInstance.goToAndStop(0, true)
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                  <span>Paramètres</span>
                </button>
                <button
                  className={`user-nav-item ${selectedUserCategory === 'securite' ? 'active' : ''}`}
                  onClick={() => {
                    switchUserCategory('securite')
                    // Déclencher l'animation dans le menu
                    if (securitySidebarLottieRef.current) {
                      const lottieInstance = securitySidebarLottieRef.current.animationItem || securitySidebarLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                    // Déclencher l'animation dans le titre
                    if (securityTitleLottieRef.current) {
                      const lottieInstance = securityTitleLottieRef.current.animationItem || securityTitleLottieRef.current
                      if (lottieInstance && lottieInstance.goToAndPlay) {
                        lottieInstance.goToAndPlay(0, true)
                      }
                    }
                  }}
                >
                  {securityAnimation && (
                    <div className="user-nav-icon">
                      <Lottie 
                        lottieRef={securitySidebarLottieRef}
                        animationData={securityAnimation} 
                        loop={false}
                        autoplay={false}
                        style={{ width: 20, height: 20 }}
                        onComplete={() => {
                          if (securitySidebarLottieRef.current) {
                            const lottieInstance = securitySidebarLottieRef.current.animationItem || securitySidebarLottieRef.current
                            if (lottieInstance && lottieInstance.goToAndStop) {
                              lottieInstance.goToAndStop(0, true)
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                  <span>Sécurité</span>
                </button>
                {adminRoleState(user, { loading, profileError }) === 'admin' && isAdminDisplay(user) ? (
                  <button
                    type="button"
                    className="user-nav-item"
                    onClick={() => navigate('/admin')}
                  >
                    <div className="user-nav-icon">
                      <ShieldGlyph size={20} />
                    </div>
                    <span>Administration</span>
                  </button>
                ) : null}
              </nav>
            </div>
            <div className="user-main-content">
              {displayedUserCategory === 'profil' && (
                <div className={userCategoryClass()}>
                  <h2 className="user-category-title">
                    {userAnimation && (
                      <div className="user-title-icon">
                        <Lottie 
                          lottieRef={titleLottieRef}
                          animationData={userAnimation} 
                          loop={false}
                          autoplay={false}
                          style={{ width: 28, height: 28 }}
                          onComplete={() => {
                            if (titleLottieRef.current) {
                              const lottieInstance = titleLottieRef.current.animationItem || titleLottieRef.current
                              if (lottieInstance && lottieInstance.goToAndStop) {
                                lottieInstance.goToAndStop(0, true)
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                    <span>Profil</span>
                  </h2>
                  {user && (
                    <div className="user-info-section">
                      <div className="user-info-item">
                        <label>Email</label>
                        <p>{user.email}</p>
                      </div>
                      <div className="user-info-item">
                        <label>ID utilisateur</label>
                        <p>{user.id}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {displayedUserCategory === 'personnaliser' && (
                <div className={userCategoryClass()}>
                  <h2 className="user-category-title">
                    {personalizeAnimation && (
                      <div className="user-title-icon">
                        <Lottie 
                          lottieRef={personalizeTitleLottieRef}
                          animationData={personalizeAnimation} 
                          loop={false}
                          autoplay={false}
                          style={{ width: 28, height: 28 }}
                          onComplete={() => {
                            if (personalizeTitleLottieRef.current) {
                              const lottieInstance = personalizeTitleLottieRef.current.animationItem || personalizeTitleLottieRef.current
                              if (lottieInstance && lottieInstance.goToAndStop) {
                                lottieInstance.goToAndStop(0, true)
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                    <span>Personnaliser</span>
                  </h2>
                  <div className="settings-section">
                    <div className="settings-group">
                      <h3>Fond animé</h3>
                      <p className="settings-description">Choisissez un fond animé pour personnaliser votre environnement</p>
                      
                      <div className="background-note">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px', marginRight: '8px', flexShrink: 0 }}>
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
                        </svg>
                        <span>Note : Les fonds d'écran personnalisés ne sont pas compatibles avec le thème switcher, sauf pour le fond par défaut.</span>
                      </div>
                      
                      <div className="background-grid">
                        {availableBackgrounds.map((bg) => (
                          <div
                            key={bg.id}
                            className={`background-card ${selectedBackground === bg.id ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedBackground(bg.id)
                              localStorage.setItem('selectedBackground', bg.id)
                            }}
                          >
                            <div className="background-preview">
                              {bg.id === 'default' ? (
                                <div className="preview-default-bg"></div>
                              ) : (
                                <BackgroundPreview backgroundId={bg.id} />
                              )}
                            </div>
                            <div className="background-info">
                              <h4>{bg.name}</h4>
                              <p>{bg.description}</p>
                            </div>
                            {selectedBackground === bg.id && (
                              <div className="background-check">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {displayedUserCategory === 'parametres' && (
                <div className={userCategoryClass()}>
                  <h2 className="user-category-title">
                    {settingsAnimation && (
                      <div className="user-title-icon">
                        <Lottie 
                          lottieRef={settingsTitleLottieRef}
                          animationData={settingsAnimation} 
                          loop={false}
                          autoplay={false}
                          style={{ width: 28, height: 28 }}
                          onComplete={() => {
                            if (settingsTitleLottieRef.current) {
                              const lottieInstance = settingsTitleLottieRef.current.animationItem || settingsTitleLottieRef.current
                              if (lottieInstance && lottieInstance.goToAndStop) {
                                lottieInstance.goToAndStop(0, true)
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                    <span>Paramètres</span>
                  </h2>
                  <div className="settings-section">
                    <div className="settings-group">
                      <h3>Notifications</h3>
                      <p className="settings-description">Gérez vos préférences de notifications</p>
                      
                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Activer toutes les notifications</label>
                            <p className="settings-hint">Active ou désactive toutes les notifications</p>
                          </div>
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={notificationsEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked
                                setNotificationsEnabled(enabled)
                                // Désactiver toutes les notifications individuelles si on désactive tout
                                if (!enabled) {
                                  setPriceChangeNotifications(false)
                                  setPriceUpdateNotifications(false)
                                  setErrorNotifications(false)
                                  setBudgetExceededNotifications(false)
                                  setProductSavedNotifications(false)
                                  setProductImportedNotifications(false)
                                  localStorage.setItem('budgetExceededNotifications', 'false')
                                  localStorage.setItem('productSavedNotifications', 'false')
                                  localStorage.setItem('productImportedNotifications', 'false')
                                } else {
                                  // Réactiver les notifications individuelles si on active tout
                                  setPriceChangeNotifications(true)
                                  setPriceUpdateNotifications(true)
                                  setErrorNotifications(true)
                                  setBudgetExceededNotifications(true)
                                  setProductSavedNotifications(true)
                                  setProductImportedNotifications(true)
                                  localStorage.setItem('budgetExceededNotifications', 'true')
                                  localStorage.setItem('productSavedNotifications', 'true')
                                  localStorage.setItem('productImportedNotifications', 'true')
                                }
                              }}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </div>

                      <div className="settings-divider"></div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Notifications de changement de prix</label>
                            <p className="settings-hint">Recevoir des notifications lors des fluctuations de prix</p>
                          </div>
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={priceChangeNotifications && notificationsEnabled}
                              disabled={!notificationsEnabled}
                              onChange={(e) => setPriceChangeNotifications(e.target.checked)}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Notifications d'actualisation des prix</label>
                            <p className="settings-hint">Recevoir un résumé après une actualisation des prix</p>
                          </div>
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={priceUpdateNotifications && notificationsEnabled}
                              disabled={!notificationsEnabled}
                              onChange={(e) => setPriceUpdateNotifications(e.target.checked)}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Notifications d'erreur</label>
                            <p className="settings-hint">Recevoir des notifications en cas d'erreur</p>
                          </div>
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={errorNotifications && notificationsEnabled}
                              disabled={!notificationsEnabled}
                              onChange={(e) => setErrorNotifications(e.target.checked)}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Notifications de budget dépassé</label>
                            <p className="settings-hint">Recevoir une alerte quand le budget d'un panier est dépassé</p>
                          </div>
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={budgetExceededNotifications && notificationsEnabled}
                              disabled={!notificationsEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked
                                setBudgetExceededNotifications(enabled)
                                localStorage.setItem('budgetExceededNotifications', enabled.toString())
                              }}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                    </div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Notifications de sauvegarde produit</label>
                            <p className="settings-hint">Recevoir une confirmation quand un produit est mis de côté</p>
                          </div>
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={productSavedNotifications && notificationsEnabled}
                              disabled={!notificationsEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked
                                setProductSavedNotifications(enabled)
                                localStorage.setItem('productSavedNotifications', enabled.toString())
                              }}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                    </div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Notifications d'import produit</label>
                            <p className="settings-hint">Recevoir une confirmation quand un produit est importé depuis la bibliothèque</p>
                          </div>
                          <label className="switch">
                            <input 
                              type="checkbox" 
                              checked={productImportedNotifications && notificationsEnabled}
                              disabled={!notificationsEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked
                                setProductImportedNotifications(enabled)
                                localStorage.setItem('productImportedNotifications', enabled.toString())
                              }}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                    </div>
                  </div>

                    <div className="settings-group">
                      <div className="settings-group-header">
                        {privacyAnimation && (
                          <div className="settings-group-icon">
                            <Lottie 
                              animationData={privacyAnimation} 
                              loop={true}
                              autoplay={true}
                              style={{ width: 24, height: 24 }}
                            />
                          </div>
                        )}
                        <h3>Confidentialité</h3>
                      </div>
                      <p className="settings-description">Gérez qui peut voir vos posts et votre profil</p>
                      
                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Visibilité des posts</label>
                            <p className="settings-hint">Définissez qui peut voir vos posts</p>
                          </div>
                          <select 
                            className="settings-select"
                            value={postVisibility}
                            onChange={async (e) => {
                              const value = e.target.value
                              setPostVisibility(value)
                              localStorage.setItem('postVisibility', value)
                              // Sauvegarder dans le backend
                              if (user?.id) {
                                try {
                                  await socialService.updateProfile(user.id, { post_visibility: value })
                                  queryClient.invalidateQueries(['social-user-profile', user.id])
                                } catch (error) {
                                  console.error('Erreur lors de la sauvegarde de la visibilité des posts:', error)
                                }
                              }
                            }}
                          >
                            <option value="public">Public (tout le monde)</option>
                            <option value="friends">Amis uniquement</option>
                            <option value="private">Privé (moi uniquement)</option>
                          </select>
                        </div>
                      </div>

                      <div className="settings-divider"></div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Visibilité du profil</label>
                            <p className="settings-hint">Définissez qui peut voir votre profil</p>
                          </div>
                          <select 
                            className="settings-select"
                            value={profileVisibility}
                            onChange={async (e) => {
                              const value = e.target.value
                              setProfileVisibility(value)
                              localStorage.setItem('profileVisibility', value)
                              // Sauvegarder dans le backend
                              if (user?.id) {
                                try {
                                  await socialService.updateProfile(user.id, { profile_visibility: value })
                                  queryClient.invalidateQueries(['social-user-profile', user.id])
                                } catch (error) {
                                  console.error('Erreur lors de la sauvegarde de la visibilité du profil:', error)
                                }
                              }
                            }}
                          >
                            <option value="public">Public (tout le monde)</option>
                            <option value="friends">Amis uniquement</option>
                            <option value="private">Privé (moi uniquement)</option>
                          </select>
                        </div>
                      </div>

                      <div className="settings-divider"></div>

                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label">Visibilité de la liste d'amis</label>
                            <p className="settings-hint">Définissez qui peut voir votre liste d'amis</p>
                          </div>
                          <select 
                            className="settings-select"
                            value={friendsVisibility}
                            onChange={async (e) => {
                              const value = e.target.value
                              setFriendsVisibility(value)
                              localStorage.setItem('friendsVisibility', value)
                              if (user?.id) {
                                try {
                                  await socialService.updateProfile(user.id, { friends_visibility: value })
                                  queryClient.invalidateQueries(['social-user-profile', user.id])
                                } catch (error) {
                                  console.error('Erreur lors de la sauvegarde de la visibilité des amis:', error)
                                }
                              }
                            }}
                          >
                            <option value="public">Public (tout le monde)</option>
                            <option value="friends">Amis uniquement</option>
                            <option value="private">Privé (moi uniquement)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="settings-group">
                      <h3>Actualisation des prix</h3>
                      <p className="settings-description">Configurez la méthode d'actualisation des prix par défaut</p>
                      
                      <div className="settings-item">
                        <div className="settings-item-content">
                          <div>
                            <label className="settings-label" htmlFor="price-update-method">Méthode d'actualisation des prix par défaut</label>
                            <p className="settings-hint">Choisissez la méthode utilisée pour actualiser les prix</p>
                          </div>
                          <select
                            id="price-update-method"
                            className="settings-select"
                            value={defaultPriceUpdateMethod}
                            onChange={(e) => {
                              const newValue = e.target.value
                              setDefaultPriceUpdateMethod(newValue)
                              localStorage.setItem('defaultPriceUpdateMethod', newValue)
                              // Déclencher un événement personnalisé pour notifier les autres composants
                              window.dispatchEvent(new Event('defaultPriceUpdateMethodChanged'))
                            }}
                          >
                            <option value="automatique">Automatique</option>
                            <option value="extension">Extension navigateur</option>
                            <option value="client">Extraction client</option>
                            <option value="serveur">Extraction serveur</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {displayedUserCategory === 'securite' && (
                <div className={userCategoryClass()}>
                  <h2 className="user-category-title">
                    {securityAnimation && (
                      <div className="user-title-icon">
                        <Lottie 
                          lottieRef={securityTitleLottieRef}
                          animationData={securityAnimation} 
                          loop={false}
                          autoplay={false}
                          style={{ width: 28, height: 28 }}
                          onComplete={() => {
                            if (securityTitleLottieRef.current) {
                              const lottieInstance = securityTitleLottieRef.current.animationItem || securityTitleLottieRef.current
                              if (lottieInstance && lottieInstance.goToAndStop) {
                                lottieInstance.goToAndStop(0, true)
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                    <span>Sécurité</span>
                  </h2>
                  <div className="security-section">
                    <div className="security-item">
                      <h3>Modifier le mot de passe</h3>
                      <p className="security-description">Changez votre mot de passe pour renforcer la sécurité de votre compte.</p>
                      <button
                        type="button"
                        className="password-form-toggle"
                        aria-expanded={showPasswordForm}
                        aria-controls="password-change-form"
                        onClick={() => setShowPasswordForm(!showPasswordForm)}
                      >
                        {showPasswordForm ? 'Masquer' : 'Modifier le mot de passe'}
                      </button>
                      {showPasswordForm && (
                        <form
                          id="password-change-form"
                          className="password-form"
                          autoComplete="on"
                          onSubmit={async (e) => {
                            e.preventDefault()
                            setPasswordError('')

                            if (newPassword !== confirmPassword) {
                              setPasswordError('Les mots de passe ne correspondent pas')
                              return
                            }

                            if (newPassword.length < 8) {
                              setPasswordError('Le mot de passe doit contenir au moins 8 caractères')
                              return
                            }

                            try {
                              await authService.updatePassword(currentPassword, newPassword)
                              addNotification({
                                type: 'success',
                                title: 'Succès',
                                message: 'Mot de passe mis à jour avec succès',
                                showToast: true
                              })
                              setShowPasswordForm(false)
                              setCurrentPassword('')
                              setNewPassword('')
                              setConfirmPassword('')
                              setPasswordError('')
                            } catch (error) {
                              setPasswordError(error.response?.data?.detail || 'Erreur lors de la mise à jour du mot de passe')
                            }
                          }}
                        >
                          <div className="form-group">
                            <label htmlFor="current-password">Mot de passe actuel</label>
                            <input
                              id="current-password"
                              name="current-password"
                              type="password"
                              autoComplete="current-password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              required
                              placeholder="Entrez votre mot de passe actuel"
                              aria-invalid={passwordError && !passwordError.includes('correspondent') && !passwordError.includes('8 caractères') ? 'true' : 'false'}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="new-password">Nouveau mot de passe</label>
                            <input
                              id="new-password"
                              name="new-password"
                              type="password"
                              autoComplete="new-password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              required
                              placeholder="Entrez votre nouveau mot de passe"
                              minLength={8}
                              aria-invalid={passwordError.includes('8 caractères') ? 'true' : 'false'}
                            />
                            <small>Au moins 8 caractères, une majuscule, une minuscule et un chiffre</small>
                          </div>
                          <div className="form-group">
                            <label htmlFor="confirm-new-password">Confirmer le nouveau mot de passe</label>
                            <input
                              id="confirm-new-password"
                              name="confirm-password"
                              type="password"
                              autoComplete="new-password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              required
                              placeholder="Confirmez votre nouveau mot de passe"
                              minLength={8}
                              aria-invalid={passwordError.includes('correspondent') ? 'true' : 'false'}
                            />
                          </div>
                          {passwordError && (
                            <div className="password-error" role="alert">
                              {passwordError}
                            </div>
                          )}
                          <button type="submit" className="password-form-submit">
                            Enregistrer
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
              </div>
            )}

            <AppDock
              onAppSelect={handleDockSelect}
              selectedApp={activeApp}
            />

        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal glass-modal modal-create" onClick={(e) => e.stopPropagation()}>
              <h2>{t('createNewCart')}</h2>
              <form onSubmit={handleCreateCart}>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder={t('cartName')}
                    value={newCartName}
                    onChange={(e) => {
                      setNewCartName(e.target.value)
                      if (createFormError) setCreateFormError('')
                      if (createMutation.isError) createMutation.reset()
                    }}
                    autoFocus
                    disabled={createMutation.isPending}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={t('budgetOptional')}
                    value={newCartBudget}
                    onChange={(e) => {
                      setNewCartBudget(e.target.value)
                      if (createMutation.isError) createMutation.reset()
                    }}
                    disabled={createMutation.isPending}
                  />
                </div>
                {(createFormError || createMutation.isError) && (
                  <p className="modal-create-error" role="alert">
                    {createFormError || getApiErrorMessage(createMutation.error, 'Impossible de créer le panier.')}
                  </p>
                )}
                <div className="modal-actions">
                  <LiquidGlassButton
                    type="button"
                    onClick={closeCreateModal}
                    className="modal-create-icon-btn"
                    aria-label="Annuler"
                  >
                    <CreateCartCancelGlyph />
                  </LiquidGlassButton>
                  <LiquidGlassButton
                    type="submit"
                    className="modal-create-icon-btn"
                    aria-label="Créer le panier"
                    aria-busy={createMutation.isPending || undefined}
                    disabled={createMutation.isPending}
                  >
                    {createMutation.isPending ? (
                      <span className="modal-create-spinner" aria-hidden="true" />
                    ) : (
                      <CreateCartSubmitGlyph />
                    )}
                  </LiquidGlassButton>
                </div>
              </form>
            </div>
          </div>
        )}

        {showRenameModal && (
          <div className="modal-overlay" onClick={() => setShowRenameModal(false)}>
            <div className="modal glass-modal modal-rename" onClick={(e) => e.stopPropagation()}>
              <h2>{t('renameCart')}</h2>
              <form onSubmit={handleRenameCart}>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Nouveau nom"
                    value={newCartName}
                    onChange={(e) => setNewCartName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <LiquidGlassButton
                    type="button"
                    onClick={() => {
                      setShowRenameModal(false)
                      setSelectedCart(null)
                      setNewCartName('')
                    }}
                    className="btn-secondary"
                  >
                    <img src="/Icon do not disturb alt.png" alt="Annuler" />
                  </LiquidGlassButton>
                  <LiquidGlassButton type="submit" className="btn-primary">
                    <img src="/Icon check.png" alt="Renommer" />
                  </LiquidGlassButton>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            if (confirmModal.onCancel) confirmModal.onCancel()
            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
          }}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
          title={confirmModal.title}
          message={confirmModal.message}
        />
      </div>

      {contextMenu.visible && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseContextMenu}
          onAction={handleContextMenuAction}
          showCompare
        />
      )}

      <CompareCartPicker
        isOpen={showCompareModal}
        onClose={() => {
          setShowCompareModal(false)
          setSelectedCartForCompare(null)
        }}
        sourceCart={selectedCartForCompare}
        carts={carts}
        onSelect={(cart) => handleCompareCart(selectedCartForCompare, cart)}
      />
    </div>
  )
}

export default CartsPage